import type {
  CommissionItemIssue,
  CommissionPayoutView,
  EligibleCommissionGroup,
  EligibleCommissionRow,
  WaitingCommissionRow,
} from '@fitting-room/contracts'
import { BOUTIQUE_LOCALE, boutiqueDayOf } from './boutique'
import { formatCents, formatRate } from './orders'

/**
 * The commission screen's words and arithmetic, kept out of the page so they
 * can be tested. `docs/22-back-office.md` § "Commission".
 */

/** What has happened to an order since its commission was calculated, as the payout flags it. */
export const ISSUE_LABEL: Record<CommissionItemIssue, string> = {
  not_paid_in_full: 'No longer paid in full',
  not_picked_up: 'Not picked up',
  canceled: 'Order canceled',
  base_changed: 'Lines changed since',
  sales_staff_changed: 'Bridal stylist changed since',
}

/** "Oct 4, 2026" — a calendar day, read back as written, never shifted by a zone. */
export function shortDay(day: string): string {
  const at = new Date(`${day}T12:00:00.000Z`)
  if (Number.isNaN(at.getTime())) return day
  return new Intl.DateTimeFormat(BOUTIQUE_LOCALE, { dateStyle: 'medium', timeZone: 'UTC' }).format(at)
}

/**
 * A payout in one line, the way the owner says it: "Oct 4, 2026 · $1,240.00
 * payable to Tayah" while it is pending, "… paid to Tayah" once confirmed —
 * dated the day it was calculated, or the day it was confirmed.
 */
export function payoutHeadline(payout: Pick<CommissionPayoutView, 'status' | 'createdAt' | 'confirmedAt' | 'totalMinor' | 'staffName'>): string {
  const confirmed = payout.status === 'confirmed'
  const day = boutiqueDayOf(confirmed ? (payout.confirmedAt ?? payout.createdAt) : payout.createdAt)
  return `${shortDay(day)} · ${formatCents(payout.totalMinor)} ${confirmed ? 'paid' : 'payable'} to ${payout.staffName}`
}

/**
 * When an eligible order settled, as its row says it: "picked up Oct 9, 2026",
 * and the day it was paid in full beside it only when that was another day —
 * the balance is taken at pickup, so a second date is worth a look. An order
 * with nothing to pick up settled the day it was paid in full.
 */
export function settledWords(row: Pick<EligibleCommissionRow, 'pickedUpOn' | 'paidInFullOn'>): string {
  if (!row.pickedUpOn) return `paid in full ${shortDay(row.paidInFullOn)}, nothing to pick up`
  if (row.pickedUpOn === row.paidInFullOn) return `picked up ${shortDay(row.pickedUpOn)}`
  return `picked up ${shortDay(row.pickedUpOn)} · paid in full ${shortDay(row.paidInFullOn)}`
}

/** Why an order isn't commissionable yet, in a few words: "$1,317.00 due · 1 to pick up". */
export function waitingWords(row: Pick<WaitingCommissionRow, 'reasons' | 'balanceMinor' | 'linesToPickUp'>): string {
  const parts: string[] = []
  if (row.reasons.includes('balance_due')) parts.push(`${formatCents(row.balanceMinor)} due`)
  if (row.reasons.includes('not_picked_up')) {
    parts.push(row.linesToPickUp === 1 ? '1 piece to pick up' : `${row.linesToPickUp} pieces to pick up`)
  }
  return parts.join(' · ')
}

/** "10% of $2,400.00". */
export function rateOfBase(rateBps: number, baseMinor: number): string {
  return `${formatRate(rateBps)} of ${formatCents(baseMinor)}`
}

/** How many of a payout's orders have changed since it was calculated. */
export function flaggedItems(payout: Pick<CommissionPayoutView, 'items'>): number {
  return payout.items.filter((item) => item.issues.length > 0).length
}

/** The orders picked that are still on the list, and what they come to. */
export function selectionSummary(
  groups: readonly EligibleCommissionGroup[],
  selected: ReadonlySet<string>,
): { orderIds: string[]; count: number; totalMinor: number; people: number } {
  const orderIds: string[] = []
  let totalMinor = 0
  let people = 0
  for (const group of groups) {
    let picked = 0
    for (const row of group.rows) {
      if (!selected.has(row.orderId)) continue
      orderIds.push(row.orderId)
      totalMinor += row.amountMinor
      picked += 1
    }
    if (picked > 0) people += 1
  }
  return { orderIds, count: orderIds.length, totalMinor, people }
}

/** Whether a person's orders are all picked, some, or none — what their select-all box shows. */
export function groupSelection(group: Pick<EligibleCommissionGroup, 'rows'>, selected: ReadonlySet<string>): 'all' | 'some' | 'none' {
  const picked = group.rows.filter((row) => selected.has(row.orderId)).length
  if (picked === 0) return 'none'
  return picked === group.rows.length ? 'all' : 'some'
}

/** Select-all for one person: picks every order of theirs, or, when all are picked, none. */
export function toggleGroup(group: Pick<EligibleCommissionGroup, 'rows'>, selected: ReadonlySet<string>): Set<string> {
  const next = new Set(selected)
  const all = groupSelection(group, selected) === 'all'
  for (const row of group.rows) {
    if (all) next.delete(row.orderId)
    else next.add(row.orderId)
  }
  return next
}

/** One order on or off. */
export function toggleOrder(orderId: string, selected: ReadonlySet<string>): Set<string> {
  const next = new Set(selected)
  if (next.has(orderId)) next.delete(orderId)
  else next.add(orderId)
  return next
}
